import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, Info, Warning, WarningCircle, X } from '@phosphor-icons/react';
import { cn } from '../../../lib/cn';
import { Icon } from '../../Icon';
import { motionDuration, prefersReducedMotion } from './platform';
/* The app's toast is not the library's toast: inverted dark surface, bottom
   right on desktop, centred under the header on mobile. `system.css` is a
   vendored copy that has to stay re-syncable, so the difference lives here.
   Imported by the component, the way Table.jsx imports its own tablecards.css. */
import '../../../patterns/toast.css';

/**
 * Toast, bottom right on desktop, four severities.
 *
 * CSS: `desktop/system.css` `.dsk-toast-region`, `.dsk-toast`,
 * `.dsk-toast--success|--error|--warning|--info`, `.dsk-toast__icon`,
 * `__body`, `__title`, `__text`, `__close`, `__timer`, `.is-leaving`.
 * Behaviour: ports BEHAVIOUR #8 as described in the CSS block that documents
 * it (the region, the timer, the pause on hover and focus, the close button and
 * the live-region announcement).
 *
 * Doctrine the library CSS encodes and this must not fight: the surface never
 * changes with severity. A solid red panel is the same mistake as a filled red
 * input, it shouts and stops being readable in dark.
 *
 * `patterns/toast.css` then re-skins that same markup for the app: one
 * inverted dark surface with white text in BOTH themes, severity carried by
 * the glyph colour alone (green check, red error sign), and the 3px bar and
 * the tinted glyph chip dropped. It also moves the region — bottom right on
 * desktop, centred below the header on mobile — and scales the whole thing
 * down under 1024. Everything about WHY is documented there; the only thing
 * this file owes it is the `dsa-` hooks below.
 *
 * PAUSE. The drain bar is paused by CSS on `:hover` and `:focus-within`. The
 * JS timeout has to pause in the same breath or the two disagree about whether
 * the toast is counting down, so the remaining life is banked on enter and
 * restarted on leave.
 *
 * REDUCED MOTION. The CSS drops the slide and the drain; this drops the
 * matching exit wait, exactly as Drawer does.
 *
 * ANNOUNCEMENT. The region is a live region. Errors and warnings are
 * `assertive` because they interrupt what the user was doing; success and info
 * are `polite`. `aria-atomic` keeps a toast from being read a word at a time as
 * it animates in.
 *
 * Replaces react-toastify. The hook's surface is deliberately familiar:
 *   const toast = useToast();
 *   toast.success('Listing published');
 *   toast.error({ title: 'Could not save', text: 'Try again in a moment.' });
 */

const ToastContext = createContext(null);

const DEFAULT_LIFE = 5000;
const LEAVE_MS = 160; /* matches the .is-leaving animation in system.css */

const SEVERITY = {
  success: { className: 'dsk-toast--success', glyph: CheckCircle, live: 'polite' },
  error: { className: 'dsk-toast--error', glyph: WarningCircle, live: 'assertive' },
  warning: { className: 'dsk-toast--warning', glyph: Warning, live: 'assertive' },
  info: { className: 'dsk-toast--info', glyph: Info, live: 'polite' },
};

let nextId = 0;

/* WHERE THE REGION SITS IS MEASURED, NOT ASSUMED.
 *
 * `patterns/toast.css` parks the region under the sticky top bar on mobile and
 * above the floating theme toggle on desktop. Neither offset is a constant a
 * stylesheet can add up:
 *
 *   HEADER. `MobileTopBar` renders a bare span when it is given neither `back`
 *   nor `menu`, which drops the 44px control track and collapses the bar from
 *   69px to ~61px; `AuthShell` renders no mobile bar at all, on nine routes.
 *
 *   FLOOR. `AuthShell` and `StatusShell` DO render `.dsk-shell-themetoggle`,
 *   fixed in the bottom-right corner at `--ds-space-5`, 44px square, z-index
 *   `--ds-z-sticky` 1100 — under the toast's 1700 — so a toast anchored to that
 *   corner covers the toggle and swallows its clicks for its whole life. Those
 *   are also the routes that fire the most toasts.
 *
 * So the two lengths are read off the live elements. Both are written as px on
 * the region and both default to 0px in CSS, so a missing bar or missing toggle
 * degrades to the plain gutter rather than to a wrong offset.
 */
const TOPBAR_SELECTOR = '.ds-appshell__topbar';
const FLOATING_TOGGLE_SELECTOR = '.ds-shell-themetoggle, .dsk-shell-themetoggle';

/* useLayoutEffect warns when there is no DOM; the portal below is already
   guarded the same way. */
const useIsomorphicLayoutEffect = typeof document !== 'undefined' ? useLayoutEffect : useEffect;

/** One toast. Owns its own timer so pausing one does not pause the stack. */
function ToastItem({ toast, onDismiss }) {
  const { className, glyph, live } = SEVERITY[toast.severity] || SEVERITY.info;
  const remaining = useRef(toast.duration);
  const startedAt = useRef(0);
  const timer = useRef(null);

  const clear = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const start = useCallback(() => {
    if (!toast.duration || toast.duration === Infinity) return;
    clear();
    startedAt.current = Date.now();
    timer.current = setTimeout(() => onDismiss(toast.id), remaining.current);
  }, [toast.duration, toast.id, onDismiss, clear]);

  const pause = useCallback(() => {
    if (!timer.current) return;
    remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current));
    clear();
  }, [clear]);

  useEffect(() => {
    start();
    return clear;
  }, [start, clear]);

  return (
    <div
      /* `dsa-toast` is the override hook. It carries no styling of its own; it
         exists so patterns/toast.css can write `.dsk-toast.dsa-toast` and beat
         the vendored one-class rules on specificity rather than on load order,
         which the bundler owns and can reorder. */
      className={cn('dsk-toast', 'dsa-toast', className, toast.leaving && 'is-leaving')}
      /* The live semantics ride on the toast itself, not on a wrapper inside
         the region: `.dsk-toast-region` is the flex column that spaces the
         stack, so an extra wrapper element would swallow its gap. role="alert"
         is implicitly assertive and role="status" implicitly polite, and both
         announce on insertion, which is exactly when a toast appears. */
      role={live === 'assertive' ? 'alert' : 'status'}
      aria-atomic="true"
      /* The CSS reads the life from this custom property; it is the documented
         API of `.dsk-toast__timer`, not a themeable value. */
      style={{ '--dsk-toast-life': `${toast.duration}ms` }}
      onMouseEnter={pause}
      onMouseLeave={start}
      onFocus={pause}
      onBlur={start}
    >
      <span className="dsk-toast__icon">
        {/* `sm` is 16px, one step under the default. The glyph sits beside a
            14px title, and the size belongs to the Icon component rather than
            to a font-size the stylesheet reaches through `.dsk-icon` to set:
            Phosphor renders at `size="1em"`, so overriding it in CSS means
            targeting the svg by class from outside. Same step the close button
            already asks for. */}
        <Icon as={glyph} size="sm" />
      </span>
      <div className="dsk-toast__body">
        {toast.title ? <p className="dsk-toast__title">{toast.title}</p> : null}
        {toast.text ? <p className="dsk-toast__text">{toast.text}</p> : null}
      </div>
      <button
        type="button"
        className="dsk-toast__close"
        aria-label={toast.closeLabel || 'Dismiss notification'}
        onClick={() => onDismiss(toast.id)}
      >
        <Icon as={X} size="sm" />
      </button>
      {toast.duration && toast.duration !== Infinity && !prefersReducedMotion() ? (
        <span className="dsk-toast__timer" />
      ) : null}
    </div>
  );
}

/**
 * ToastProvider. Mount once, high in the tree. Renders the region through a
 * portal so no `overflow: hidden` ancestor can clip it and no page layout can
 * change its stacking.
 */
export function ToastProvider({ children, duration = DEFAULT_LIFE, max = 4 }) {
  const [toasts, setToasts] = useState([]);
  const regionRef = useRef(null);

  /* Measured while the region has something in it, and again on resize because
     the mobile bar and the desktop toggle swap in and out at the 1024 handoff.
     Keyed on the count, not on the array, so a pause or a leave flag does not
     re-measure; a route change that swaps the shell always arrives with a new
     toast, which is the only moment the offsets matter. */
  useIsomorphicLayoutEffect(() => {
    const region = regionRef.current;
    if (!region || toasts.length === 0) return undefined;

    const measure = () => {
      const bar = document.querySelector(TOPBAR_SELECTOR);
      const toggle = document.querySelector(FLOATING_TOGGLE_SELECTOR);
      /* The bar is sticky at top 0, so its rect bottom IS the height the toast
         has to clear. The toggle is fixed to the bottom, so what the toast has
         to clear is the gap from the viewport floor to its top edge — its own
         height plus whatever gutter the shell gave it. */
      const header = bar ? Math.round(bar.getBoundingClientRect().bottom) : 0;
      const floor = toggle
        ? Math.round(window.innerHeight - toggle.getBoundingClientRect().top)
        : 0;
      region.style.setProperty('--dsa-toast-header', `${Math.max(0, header)}px`);
      region.style.setProperty('--dsa-toast-floor', `${Math.max(0, floor)}px`);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [toasts.length]);

  const dismiss = useCallback((id) => {
    setToasts((list) => list.map((item) => (item.id === id ? { ...item, leaving: true } : item)));
    setTimeout(() => {
      setToasts((list) => list.filter((item) => item.id !== id));
    }, motionDuration(LEAVE_MS));
  }, []);

  const push = useCallback(
    (severity, input) => {
      const options = typeof input === 'string' ? { title: input } : input || {};
      nextId += 1;
      const id = options.id != null ? options.id : `dsk-toast-${nextId}`;
      const toast = {
        id,
        severity,
        title: options.title,
        text: options.text,
        closeLabel: options.closeLabel,
        duration: options.duration === undefined ? duration : options.duration,
        leaving: false,
      };
      setToasts((list) => [...list, toast].slice(-max));
      return id;
    },
    [duration, max],
  );

  const api = useMemo(
    () => ({
      toast: (input) => push((input && input.severity) || 'info', input),
      success: (input) => push('success', input),
      error: (input) => push('error', input),
      warning: (input) => push('warning', input),
      info: (input) => push('info', input),
      dismiss,
      dismissAll: () => setToasts([]),
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={regionRef}
              className="dsk-toast-region dsa-toast-region"
              role="region"
              aria-label="Notifications"
            >
              {toasts.map((item) => (
                <ToastItem key={item.id} toast={item} onDismiss={dismiss} />
              ))}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}

/**
 * useToast. Throws when the provider is missing rather than silently swallowing
 * notifications, which is the failure mode that hides a broken save.
 */
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used inside a <ToastProvider>.');
  }
  return context;
}

export default ToastProvider;
